import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { useAuthStore } from './hooks/useAuthStore';
import { useQuery } from '@tanstack/react-query';
import { onboardingService } from './services/api';

// Layouts - loaded eagerly (small, always needed)
import DashboardLayout from './components/layouts/DashboardLayout';
import AuthLayout from './components/layouts/AuthLayout';
import AdminLayout from './components/layouts/AdminLayout';

// Loading spinner component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-4 border-secondary-200"></div>
          <div className="absolute top-0 left-0 w-12 h-12 rounded-full border-4 border-primary-600 border-t-transparent animate-spin"></div>
        </div>
        <p className="text-secondary-500 text-sm font-medium">Loading...</p>
      </div>
    </div>
  );
}

// Auth Pages - Lazy loaded
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));

// Legal Pages - Lazy loaded
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));

// Onboarding - Lazy loaded
const OnboardingWizard = lazy(() => import('./pages/onboarding/OnboardingWizard'));

// Dashboard Pages - Lazy loaded
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const CustomersPage = lazy(() => import('./pages/customers/CustomersPage'));
const GeneratorsPage = lazy(() => import('./pages/generators/GeneratorsPage'));
const GeneratorDetailPage = lazy(() => import('./pages/generators/GeneratorDetailPage'));
const GeneratorEditPage = lazy(() => import('./pages/generators/GeneratorEditPage'));
const WasteProfilesPage = lazy(() => import('./pages/waste-profiles/WasteProfilesPage'));
const WasteProfileDetailPage = lazy(() => import('./pages/waste-profiles/WasteProfileDetailPage'));
const WasteProfileWizardPage = lazy(() => import('./pages/waste-profiles/WasteProfileWizardPage'));
const ManifestsPage = lazy(() => import('./pages/manifests/ManifestsPage'));
const TransportationPage = lazy(() => import('./pages/transportation/TransportationPage'));
const TransportationNewPage = lazy(() => import('./pages/transportation/TransportationNewPage'));
const FacilityMatchingPage = lazy(() => import('./pages/facilities/FacilityMatchingPage'));
const DocumentCenterPage = lazy(() => import('./pages/documents/DocumentCenterPage'));
const UploadCenterPage = lazy(() => import('./pages/documents/UploadCenterPage'));
const SDSLibraryPage = lazy(() => import('./pages/documents/SDSLibraryPage'));
const LabReportsPage = lazy(() => import('./pages/documents/LabReportsPage'));
const DocumentDetailPage = lazy(() => import('./pages/documents/DocumentDetailPage'));
const EManifestPage = lazy(() => import('./pages/emanifest/EManifestPage'));
const DispatchPage = lazy(() => import('./pages/dispatch/DispatchPage'));
const FieldJobsPage = lazy(() => import('./pages/field/FieldJobsPage'));
const LoadPlanPage = lazy(() => import('./pages/dispatch/LoadPlanPage'));
// V2 client-side analyzers — both deterministic multi-agent pipelines, no
// AI / backend calls. SDS Analyzer V2 ported from unboxed; Lab Report
// Analyzer V2 ported from hazwaste-platform's own backend engine
// (backend/src/engines/lab-report-v2/) which is also deterministic-first.
const SDSAnalyzerV2Page = lazy(() => import('./tools/SDSAnalyzerV2/ui/SDSAnalyzerV2.jsx'));
const LabReportAnalyzerPage = lazy(() => import('./tools/LabReportAnalyzerV2/ui/LabReportAnalyzerV2.jsx'));
const PhotoToProfilePage = lazy(() => import('./tools/PhotoToProfile/PhotoToProfilePage'));
const BillingPage = lazy(() => import('./pages/billing/BillingPage'));
const TransactionsPage = lazy(() => import('./pages/transactions/TransactionsPage'));
const CompliancePage = lazy(() => import('./pages/compliance/CompliancePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const BrokerAccessPage = lazy(() => import('./pages/settings/BrokerAccessPage'));
const TPAManagementPage = lazy(() => import('./pages/TPAManagementPage'));
const DisposalSitesPage = lazy(() => import('./pages/disposal-sites/DisposalSitesPage'));

const HelpPage = lazy(() => import('./pages/HelpPage'));
const RequestSampleKitPage = lazy(() => import('./pages/samples/RequestSampleKitPage'));
// Credit and Agreement Pages - Lazy loaded
const CreditApplicationPage = lazy(() => import('./pages/credit/CreditApplicationPage'));

// SDS Pages - Lazy loaded
const SDSListPage = lazy(() => import('./pages/sds/SDSListPage'));
const SDSUploadPage = lazy(() => import('./pages/sds/SDSUploadPage'));
const SDSDetailPage = lazy(() => import('./pages/sds/SDSDetailPage'));

// Admin Pages - Lazy loaded
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const InternalDashboardPage = lazy(() => import('./pages/admin/InternalDashboardPage'));
const TSDFManagementPage = lazy(() => import('./pages/admin/partners/TSDFManagementPage'));
const TransporterManagementPage = lazy(() => import('./pages/admin/partners/TransporterManagementPage'));
const TSDFFormTemplateManager = lazy(() => import('./pages/admin/partners/TSDFFormTemplateManager'));
const TransactionManagementPage = lazy(() => import('./pages/admin/TransactionManagementPage'));
const ProfileReviewPage = lazy(() => import('./pages/admin/ProfileReviewPage'));
const CustomerManagementPage = lazy(() => import('./pages/admin/CustomerManagementPage'));
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage'));
const ConfigurationPage = lazy(() => import('./pages/admin/ConfigurationPage'));
const AuthorizedAgentsPage = lazy(() => import('./pages/admin/AuthorizedAgentsPage'));
const CreditManagementPage = lazy(() => import('./pages/admin/CreditManagementPage'));
const PricingConfirmationPage = lazy(() => import('./pages/admin/PricingConfirmationPage'));
const RoutePlanningPage = lazy(() => import('./pages/admin/RoutePlanningPage'));
const RateEstimatesPage = lazy(() => import('./pages/admin/RateEstimatesPage'));
const QuoteBuilderPage = lazy(() => import('./pages/admin/QuoteBuilderPage'));

// Internal admin-only tools (ported from unboXed) - Lazy loaded
const CustomerRequestsPage = lazy(() => import('./pages/admin/CustomerRequestsPage'));
const PurchaseOrdersPage = lazy(() => import('./pages/admin/PurchaseOrdersPage'));
const SignaturesPage = lazy(() => import('./pages/admin/SignaturesPage'));
const LeadsPage = lazy(() => import('./pages/admin/LeadsPage'));
const WasteIntelligencePage = lazy(() => import('./pages/admin/WasteIntelligencePage'));
const VendorDiscoveryPage = lazy(() => import('./pages/admin/VendorDiscoveryPage'));
const ManifestPrinterPage = lazy(() => import('./pages/admin/ManifestPrinterPage'));
const LabAnalyzerPage = lazy(() => import('./pages/admin/LabAnalyzerPage'));
const LabPackPlannerPage = lazy(() => import('./pages/admin/LabPackPlannerPage'));
const LabPackDetailPage = lazy(() => import('./pages/admin/LabPackDetailPage'));
const SamGovOpportunitiesPage = lazy(() => import('./pages/admin/SamGovOpportunitiesPage'));
const SamGovOpportunityDetailPage = lazy(() => import('./pages/admin/SamGovOpportunityDetailPage'));
const SamGovPipelinePage = lazy(() => import('./pages/admin/SamGovPipelinePage'));
const SamGovKeywordsPage = lazy(() => import('./pages/admin/SamGovKeywordsPage'));

// Public signing page (no auth) - Lazy loaded
const PublicSigningPage = lazy(() => import('./pages/PublicSigningPage'));

// Public landing page — front door for unauthenticated visitors. Embeds
// the SDS Analyzer V2 inline so anyone can drop a PDF and see RCRA codes
// without signing up. Lead-gen surface.
const LandingPage = lazy(() => import('./pages/public/LandingPage'));

// Protected Route wrapper with onboarding check
function ProtectedRoute({ children, skipOnboardingCheck = false }: { children: React.ReactNode; skipOnboardingCheck?: boolean }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  // Allow pages accessed during onboarding (e.g. /sds/upload?onboarding=true)
  const searchParams = new URLSearchParams(location.search);
  const isOnboardingFlow = skipOnboardingCheck || searchParams.get('onboarding') === 'true';

  // Fetch onboarding progress — must always be fresh to avoid stale redirects
  // after user completes onboarding (default QueryClient staleTime is 5min)
  const { data: onboardingData, isLoading: onboardingLoading, isFetching } = useQuery({
    queryKey: ['onboarding-progress'],
    queryFn: () => onboardingService.getProgress(),
    enabled: isAuthenticated && !isOnboardingFlow,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    // Unauthenticated visitor at "/" gets the public landing page (which
    // embeds the SDS Analyzer V2 as a no-signup lead-gen surface) instead
    // of being bounced to /login. Every other path still redirects to login.
    if (location.pathname === '/') {
      return <LandingPage />;
    }
    return <Navigate to="/login" replace />;
  }

  // Onboarding gate. The query is configured with staleTime: 0 +
  // gcTime: 0 + refetchOnMount: 'always' so the post-completion
  // redirect bug (re-firing the redirect after onboarding finished
  // because of stale cache) doesn't recur. Also wait for isFetching
  // before deciding so a half-mounted query can't briefly show the
  // dashboard then bounce.
  if (!isOnboardingFlow && (onboardingLoading || isFetching)) {
    return <PageLoader />;
  }

  if (!isOnboardingFlow && onboardingData) {
    // axios response: { data: { success, progress } }
    const progress = (onboardingData as any)?.data?.progress;
    if (progress && progress.onboarding_complete === false) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  return <>{children}</>;
}

// Admin Protected Route wrapper - requires admin account type (internal staff only)
function AdminProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isAdmin } = useAuthStore();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Broker or Admin route guard - blocks generator accounts from broker-only features
function BrokerOrAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isBroker, isAdmin } = useAuthStore();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isBroker() && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        </Route>

        {/* Legal Pages - public, no auth required */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Public e-signature page — no auth, token-based */}
        <Route path="/sign/:token" element={<PublicSigningPage />} />

        {/* Onboarding Route - skip onboarding check to avoid redirect loop */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute skipOnboardingCheck={true}>
              <OnboardingWizard />
            </ProtectedRoute>
          }
        />

        {/* Protected Dashboard Routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/generators" element={<GeneratorsPage />} />
          <Route path="/generators/new" element={<GeneratorEditPage />} />
          <Route path="/generators/:id" element={<GeneratorDetailPage />} />
          <Route path="/generators/:id/edit" element={<GeneratorEditPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/waste-profiles" element={<WasteProfilesPage />} />
          <Route path="/waste-profiles/new" element={<Navigate to="/waste-profiles/wizard" replace />} />
          <Route path="/waste-profiles/wizard" element={<WasteProfileWizardPage />} />
          <Route path="/waste-profiles/:id" element={<WasteProfileDetailPage />} />
          <Route path="/sds" element={<SDSListPage />} />
          <Route path="/sds/upload" element={<SDSUploadPage />} />
          <Route path="/sds/:id" element={<SDSDetailPage />} />
          <Route path="/manifests" element={<ManifestsPage />} />
          <Route path="/manifests/:id" element={<ManifestsPage />} />
          <Route path="/transportation" element={<BrokerOrAdminRoute><TransportationPage /></BrokerOrAdminRoute>} />
          <Route path="/transportation/new" element={<BrokerOrAdminRoute><TransportationNewPage /></BrokerOrAdminRoute>} />
          <Route path="/facilities/match" element={<BrokerOrAdminRoute><FacilityMatchingPage /></BrokerOrAdminRoute>} />
          <Route path="/documents" element={<DocumentCenterPage />} />
          <Route path="/documents/upload" element={<UploadCenterPage />} />
          <Route path="/documents/library" element={<SDSLibraryPage />} />
          <Route path="/documents/sds" element={<SDSLibraryPage />} />
          <Route path="/documents/lab-reports" element={<LabReportsPage />} />
          <Route path="/documents/:id" element={<DocumentDetailPage />} />
          <Route path="/emanifest" element={<EManifestPage />} />
          <Route path="/dispatch" element={<DispatchPage />} />
          <Route path="/field/jobs" element={<FieldJobsPage />} />
          <Route path="/dispatch/loads/:id/plan" element={<LoadPlanPage />} />
          <Route path="/tools/sds-analyzer" element={<SDSAnalyzerV2Page />} />
          <Route path="/tools/lab-report-analyzer" element={<LabReportAnalyzerPage />} />
          <Route path="/tools/photo-to-profile" element={<PhotoToProfilePage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/broker-access" element={<BrokerAccessPage />} />
          <Route path="/tpa" element={<BrokerOrAdminRoute><TPAManagementPage /></BrokerOrAdminRoute>} />
          <Route path="/disposal-sites" element={<BrokerOrAdminRoute><DisposalSitesPage /></BrokerOrAdminRoute>} />
          <Route path="/sites" element={<Navigate to="/generators" replace />} />
          <Route path="/sample-kit" element={<RequestSampleKitPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/credit" element={<CreditApplicationPage />} />
          <Route path="/credit/apply" element={<CreditApplicationPage />} />
          <Route path="/reports" element={<ReportsPage />} />
        </Route>

        {/* Admin Routes - requires admin account type (internal staff only) */}
        <Route
          element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/internal" element={<InternalDashboardPage />} />
          <Route path="/admin/partners/tsdf" element={<TSDFManagementPage />} />
          <Route path="/admin/partners/tsdf/:tsdfId/templates" element={<TSDFFormTemplateManager />} />
          <Route path="/admin/partners/transporters" element={<TransporterManagementPage />} />
          <Route path="/admin/transactions" element={<TransactionManagementPage />} />
          <Route path="/admin/profiles" element={<ProfileReviewPage />} />
          <Route path="/admin/customers" element={<CustomerManagementPage />} />
          <Route path="/admin/reports" element={<ReportsPage />} />
          <Route path="/admin/config" element={<ConfigurationPage />} />
          <Route path="/admin/authorized-agents" element={<AuthorizedAgentsPage />} />
          <Route path="/admin/credit" element={<CreditManagementPage />} />
          <Route path="/admin/pricing" element={<PricingConfirmationPage />} />
          <Route path="/admin/route-planning" element={<RoutePlanningPage />} />
          <Route path="/admin/rate-estimates" element={<RateEstimatesPage />} />
          <Route path="/admin/quote-builder" element={<QuoteBuilderPage />} />

          {/* Internal admin-only tools (ported from unboXed) */}
          <Route path="/admin/customer-requests" element={<CustomerRequestsPage />} />
          <Route path="/admin/purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="/admin/signatures" element={<SignaturesPage />} />
          <Route path="/admin/leads" element={<LeadsPage />} />
          <Route path="/admin/intel" element={<WasteIntelligencePage />} />
          <Route path="/admin/vendors" element={<VendorDiscoveryPage />} />
          <Route path="/admin/manifest-printer" element={<ManifestPrinterPage />} />
          <Route path="/admin/lab-analyzer" element={<LabAnalyzerPage />} />
          <Route path="/admin/lab-packs" element={<LabPackPlannerPage />} />
          <Route path="/admin/lab-packs/:id" element={<LabPackDetailPage />} />

          {/* SAM.gov Contract Opportunities — government-contract pipeline */}
          <Route path="/admin/sam-gov" element={<SamGovOpportunitiesPage />} />
          <Route path="/admin/sam-gov/opportunities/:id" element={<SamGovOpportunityDetailPage />} />
          <Route path="/admin/sam-gov/pipeline" element={<SamGovPipelinePage />} />
          <Route path="/admin/sam-gov/keywords" element={<SamGovKeywordsPage />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
